import { useState } from 'react'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDateVNFromTs } from '@rs/contracts'
import { Button, Card } from '@rs/ui'
import { CopyRef } from '../../shared/CopyRef.tsx'
import { PhoneLink } from '../az-sales/StopCell.tsx'
import { fmtStopAt, stopAddr, toStops, type Stop } from '../az-sales/order/stops.ts'
import { useCan } from '../../shared/can.ts'
import {
  confirmMyLegApi, saveMyContSealApi, saveMyPlateApi, uploadMyPodApi, type MyLegRow,
} from './api.ts'

/**
 * ══ CỔNG THẦU PHỤ ▸ 📦 ĐƠN HÀNG CỦA TÔI — chép `NccPortal.tsx:702-858` ════════════════════════
 *
 * ⭐ Bố cục Y HỆT bảng "Đơn hàng RT" nội bộ (bản cũ user 27/08: "bố trí lại HẾT để nó như mẫu"):
 *   tên khách + mã đơn · hàng hoá + số tấn + huy hiệu cont · tuyến + dịch vụ · ô lấy/giao kiểu
 *   `StopCell` (giờ đậm + KH + SĐT · tên + địa chỉ). Khác bảng nội bộ đúng HAI chỗ có chủ đích:
 *   KHÔNG có Đối tác / Tổng thu (thầu phụ không thấy NCC khác, không thấy giá bán), và có RIÊNG một
 *   cột Thao tác cho xác nhận · BKS · Cont/Seal.
 * ⚠ Cột Trạng thái CHỈ một huy hiệu nhận/chưa nhận/từ chối + ai xác nhận (bản cũ 27/08: công ty
 *   xác nhận hộ tô CAM để thầu phụ soát lại).
 * ⚠ KHÔNG có nút ĐẨY TIẾN ĐỘ chặng — bản cũ không có (`NccPortal.tsx:772-858`), điều hành chốt tiến độ; người
 *   dùng chốt 17/10 bỏ nút của spec F13 cùng cửa máy chủ của nó. Còn MỘT việc rs-erp có mà bản cũ không có: TẢI ẢNH
 *   POD ở dòng cuối ô Thao tác (máy chủ `/ncc/pod`).
 */

/** Huy hiệu loại cont — chép `contTypeBadgeCls` (`NccPortal.tsx:356`): cont lạnh viền xanh trời. */
const contBadgeCls = (contType: string): string => /rf/i.test(contType)
  ? 'inline-block mt-0.5 text-[10px] font-bold text-sky-700 bg-sky-50 border border-sky-300 rounded px-1.5'
  : 'inline-block mt-0.5 text-[10px] font-bold text-slate-500 bg-slate-100 rounded px-1.5'

/** `stopText` bản cũ (`NccPortal.tsx:349`): ga · địa chỉ · người liên hệ · số máy, nối bằng " · ". */
const stopText = (s: Stop | undefined): string =>
  [s?.station, stopAddr(s) === s?.station ? '' : stopAddr(s), s?.contact, s?.phone]
    .filter((x): x is string => x !== undefined && x.trim() !== '').join(' · ')

const StopBox = ({ stop, at, fallback }: { stop: Stop | undefined; at: string; fallback: string }): JSX.Element => {
  const { t } = useTranslation()
  return (
    <div className="w-full space-y-0.5">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="font-bold text-slate-700 text-[11px] whitespace-nowrap">{at !== '' ? fmtStopAt(at) : '—'}</span>
        {at !== '' && <span className="text-[9px] font-bold text-slate-500 bg-slate-100 rounded px-1">{t('ncc.planTag')}</span>}
      </div>
      {stop?.phone !== undefined && stop.phone !== '' && (
        <div className="flex items-center gap-1 flex-wrap">
          <PhoneLink phone={stop.phone} />
          {stop.contact !== undefined && stop.contact !== '' && (
            <span className="text-slate-500 text-[11px] whitespace-nowrap">· {stop.contact}</span>
          )}
        </div>
      )}
      <div className="text-slate-500 text-[11px] leading-tight break-words">{stopText(stop) || fallback || '—'}</div>
    </div>
  )
}

const inCls = 'border border-slate-300 rounded-lg px-1.5 py-1 text-[11px] w-full'
const saveCls = 'shrink-0 text-[10px] font-bold text-brand-600 border border-brand-200 bg-brand-50 hover:bg-brand-100 rounded-lg px-1.5 py-1'

export const OrdersTab = ({ legs, run, onReject }: {
  legs: readonly MyLegRow[]
  /** Chạy một thao tác ghi, báo lỗi lên dải đỏ đầu cổng và tải lại danh sách. */
  run: (fn: () => Promise<unknown>) => Promise<void>
  onReject: (leg: MyLegRow) => void
}): JSX.Element => {
  const { t } = useTranslation()
  // Nháp theo CHẶNG (BKS) và theo ĐƠN (Cont/Seal — một đơn nhiều chặng thì cùng một số cont).
  const canDo = useCan()
  const [plate, setPlate] = useState<Record<string, string>>({})
  const [cont, setCont] = useState<Record<string, string>>({})
  const [seal, setSeal] = useState<Record<string, string>>({})

  if (legs.length === 0) {
    return <Card className="p-6 text-center text-slate-400 italic text-sm">{t('ncc.emptyLegs')}</Card>
  }

  const plateOf = (l: MyLegRow): string => plate[l.id] ?? l.truckPlate ?? ''
  /* ⭐ Ô TRỐNG + 💾 = XOÁ BKS (L32, soát 16/10) — bản cũ `saveBks` ghi cả chuỗi rỗng ("NCC cập nhật BKS: (xóa)",
     `NccPortal.tsx:269-272`). Trước đây ô trống bấm 💾 im lặng không gọi gì, BKS gõ nhầm không gỡ được. */
  const savePlate = (l: MyLegRow): void => {
    void run(() => saveMyPlateApi(l.id, l.version, plateOf(l).trim()))
  }
  /** Bản cũ `confirmLeg`: BKS đã gõ thì lưu luôn cùng lúc nhận chuyến — MỘT lượt ghi, một dòng lịch sử (L32). */
  const takeLeg = (l: MyLegRow): void => {
    void run(() => confirmMyLegApi(l.id, l.version, plateOf(l)))
  }

  return (
    <Card className="!rounded-lg !shadow-none border-slate-200 overflow-x-auto">
      <table className="w-full text-xs table-fixed">
        <colgroup>
          <col style={{ width: 36 }} /><col style={{ width: 160 }} /><col style={{ width: 100 }} />
          <col style={{ width: 140 }} /><col style={{ width: 185 }} /><col style={{ width: 185 }} />
          <col style={{ width: 100 }} /><col style={{ width: 210 }} />
        </colgroup>
        <thead className="sticky top-0 bg-slate-50">
          <tr className="border-b divide-x divide-slate-200 border-slate-200 text-[10px] font-extrabold text-slate-400 uppercase tracking-wide">
            <th className="px-1 py-2.5 text-center">{t('ncc.thStt')}</th>
            <th className="px-3 py-2.5 text-center">{t('ncc.thCustomerOrder')}</th>
            <th className="px-3 py-2.5 text-center">{t('ncc.thCargo')}</th>
            <th className="px-3 py-2.5 text-center">{t('ncc.thRoute')}</th>
            <th className="px-3 py-2.5 text-center">{t('ncc.thPickup')}</th>
            <th className="px-3 py-2.5 text-center">{t('ncc.thDrop')}</th>
            <th className="px-3 py-2.5 text-center">{t('ncc.thStatus')}</th>
            <th className="px-3 py-2.5 text-center">{t('ncc.thAction')}</th>
          </tr>
        </thead>
        <tbody>
          {legs.map((l, i) => {
            const pickups = toStops(l.jobPickups)
            const drops = toStops(l.jobDrops)
            const pu = pickups[0]
            const dr = drops[0]
            const puAt = pu?.at ?? (l.planDate !== '' ? `${l.planDate}T08:00` : '')
            const drAt = dr?.at ?? ''
            const contNo = cont[l.jobId] ?? l.jobContNo ?? ''
            const sealNo = seal[l.jobId] ?? l.jobSealNo ?? ''
            /* Đơn đã giao / đã huỷ ⇒ máy chủ khoá "Không nhận" (C14, `guardMineOpen` — giữ theo chốt 24/10, V04061). "Nhận" /
               "Nhận lại" thì như bản cũ: mọi chặng của mình (`NccPortal.tsx:792-797`, V04055) — chỉ đơn huỷ khoá (bản cũ huỷ
               đơn là gỡ chặng). BKS, Cont/Seal và ảnh POD cũng KHÔNG khoá theo đơn đã giao. */
            const jobOpen = l.jobStatus !== 'delivered' && l.jobStatus !== 'cancelled'
            /* 💾 BKS và 💾 Cont/Seal sửa được cả sau khi đơn đã giao — bản cũ `NccPortal.tsx:806-815` (BKS) và
               `:819-829` + `saveContSeal` `:82-86` (Cont/Seal) không khoá theo trạng thái đơn, người dùng chốt 17/10.
               Chỉ đơn huỷ khoá (bản cũ huỷ đơn là gỡ chặng). */
            const fieldsOpen = l.jobStatus !== 'cancelled'
            /* BKS thêm một chốt: chặng đã TỪ CHỐI / đã huỷ thì không còn là việc của NCC (bản cũ chỉ có ô BKS ở dòng đã nhận,
               không bị từ chối — `NccPortal.tsx:794-815`; máy chủ chặn ở `saveMyTruckPlate`). Dòng từ chối vốn không bày ô. */
            const plateOpen = fieldsOpen && !l.vendorRejected && l.status !== 'cancelled'
            /* Nhận / nhận lại: như bản cũ, không xét trạng thái chặng/đơn — trừ đơn đã huỷ (`confirmMyLeg`). */
            const mayTake = fieldsOpen
            /* Không nhận: chỉ khi chặng chưa lăn bánh — khớp `vendorMayRejectLeg` (`@rs/ops`, new/assigned). */
            const mayReject = jobOpen && (l.status === 'new' || l.status === 'assigned')
            return (
              <tr key={l.id} className={`border-t border-slate-200 divide-x divide-slate-200 align-top ${i % 2 === 1 ? 'bg-slate-50/50' : 'bg-white'}`}>
                <td className="px-1 py-2 text-center text-slate-400 tabular-nums">{i + 1}</td>
                <td className="px-3 py-2 break-words">
                  <div className="font-bold text-[12px] text-slate-700">{l.customerName ?? '—'}</div>
                  <div className="font-mono text-[13px] font-bold text-brand-600 break-all">
                    {l.jobRef}<CopyRef text={l.jobRef} className="ml-1" />
                  </div>
                </td>
                <td className="px-3 py-2 break-words text-center">
                  <div className="text-[10px] text-slate-700 break-words whitespace-pre-line">{l.jobCargo ?? l.name ?? t('ncc.transport')}</div>
                  {l.jobWeightTon !== null && l.jobWeightTon > 0 && (
                    <div className="text-[11px] text-slate-500">{l.jobWeightTon.toLocaleString('vi-VN')} {t('ncc.ton')}</div>
                  )}
                  {l.jobContType !== null && <span className={contBadgeCls(l.jobContType)}>{l.jobContType}</span>}
                </td>
                <td className="px-3 py-2 break-words text-center">
                  {l.jobRouteName !== null && l.jobRouteName !== ''
                    ? <div className="font-bold text-[12px] text-slate-700 break-words">{l.jobRouteName}</div>
                    : <div className="text-[11px] text-slate-400 italic">—</div>}
                  {l.jobPackName !== null && (
                    <div className="text-[10px] font-bold text-brand-600 mt-0.5 break-words">{l.jobPackName}</div>
                  )}
                </td>
                <td className="px-3 py-2"><StopBox stop={pu} at={puAt} fallback={l.fromAddr || (l.jobPickupAddr ?? '')} /></td>
                <td className="px-3 py-2"><StopBox stop={dr} at={drAt} fallback={l.toAddr || (l.jobDropAddr ?? '')} /></td>
                <td className="px-3 py-2 text-center">
                  {l.vendorConfirmed
                    ? <span className="inline-block text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1 whitespace-nowrap">{t('ncc.confirmed')}</span>
                    : l.vendorRejected
                      ? <span title={l.vendorRejectReason ?? undefined} className="inline-block text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 rounded-lg px-2 py-1 whitespace-nowrap">{t('ncc.rejected')}</span>
                      : <span className="inline-block text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 whitespace-nowrap">{t('ncc.pendingBadge')}</span>}
                  {l.vendorConfirmed && l.vendorConfirmedAt !== null && (
                    <div className="mt-1 text-[9px] text-emerald-600">{formatDateVNFromTs(l.vendorConfirmedAt)}</div>
                  )}
                  {l.vendorConfirmed && l.vendorConfirmedVia === 'company' && (
                    <div className="mt-0.5 text-[9px] font-bold text-orange-600" title={t('ncc.confirmedByCompanyTip')}>{t('ncc.confirmedByCompany')}</div>
                  )}
                  {l.vendorConfirmed && l.vendorConfirmedVia === 'vendor' && (
                    <div className="mt-0.5 text-[9px] text-slate-400">{t('ncc.confirmedByYou')}</div>
                  )}
                </td>
                <td className="px-3 py-2">
                  {l.vendorRejected ? (
                    <div>
                      <div className="text-[10px] text-slate-400 mb-1 break-words">{l.vendorRejectReason}</div>
                      {mayTake && (
                        <button type="button" onClick={() => { takeLeg(l) }}
                                className="text-[10px] font-semibold text-brand-600 hover:underline">{t('ncc.retake')}</button>
                      )}
                    </div>
                  ) : !l.vendorConfirmed ? (
                    <div className="flex flex-col gap-1">
                      <input value={plateOf(l)} aria-label={t('ncc.plate')} placeholder={t('ncc.platePh')} className={inCls}
                             disabled={!mayTake}
                             onChange={(e) => { setPlate({ ...plate, [l.id]: e.target.value }) }} />
                      <div className="flex gap-1">
                        {mayTake && canDo('vendorPortal.orders', 'edit') && <Button size="sm" onClick={() => { takeLeg(l) }}>{t('ncc.btnConfirm')}</Button>}
                        {mayReject && (
                          <button type="button" onClick={() => { onReject(l) }}
                                  className="text-[10px] font-bold text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 rounded-lg px-2 py-1 whitespace-nowrap">
                            {t('ncc.btnReject')}
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-1 items-center">
                      {/* BKS sửa được cả sau khi đã xác nhận — thầu phụ điều xe sau (bản cũ). */}
                      <input value={plateOf(l)} aria-label={t('ncc.plate')} placeholder={t('ncc.plateEmpty')} className={inCls}
                             disabled={!plateOpen}
                             onChange={(e) => { setPlate({ ...plate, [l.id]: e.target.value }) }} />
                      {plateOpen && <button type="button" title={t('ncc.savePlate')} className={saveCls} onClick={() => { savePlate(l) }}>💾</button>}
                    </div>
                  )}
                  {/* Cont/Seal — thầu phụ tự nhập, ghi lên ĐƠN cho công ty khỏi nhập lại (bản cũ user 28/07). */}
                  <div className="mt-1.5 flex gap-1 items-center">
                    <input value={contNo} aria-label={t('ncc.contNo')} placeholder={t('ncc.contNo')} className={inCls}
                           disabled={!fieldsOpen}
                           onChange={(e) => { setCont({ ...cont, [l.jobId]: e.target.value }) }} />
                    <input value={sealNo} aria-label={t('ncc.seal')} placeholder={t('ncc.seal')} className={inCls}
                           disabled={!fieldsOpen}
                           onChange={(e) => { setSeal({ ...seal, [l.jobId]: e.target.value }) }} />
                    {fieldsOpen && (
                      <button type="button" title={t('ncc.save')} className={saveCls}
                              onClick={() => { void run(() => saveMyContSealApi(l.id, contNo.trim(), sealNo.trim())) }}>💾</button>
                    )}
                  </div>
                  {((l.jobContNo ?? '') !== '' || (l.jobSealNo ?? '') !== '') && (
                    <div className="text-[9px] text-emerald-600 mt-0.5">{t('ncc.synced')}</div>
                  )}
                  {/* Ảnh POD (rs-erp F13) — gọn một dòng dưới cùng. KHÔNG có nút tiến độ chặng (chốt 17/10, xem đầu tệp). */}
                  <div className="mt-1.5 flex items-center gap-2 text-[10px]">
                    <label className="ml-auto text-sky-600 cursor-pointer hover:underline">
                      {t('az.detail.addPhoto')}
                      <input type="file" multiple accept="image/*,application/pdf" className="hidden"
                             aria-label={t('az.detail.addPhoto')}
                             onChange={(e) => {
                               const files = e.target.files
                               if (files === null || files.length === 0) return
                               void run(async () => {
                                 for (const file of Array.from(files)) await uploadMyPodApi(l.id, file)
                               })
                             }} />
                    </label>
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </Card>
  )
}
